import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapTrifold, GoogleLogo, NavigationArrow, Broadcast, StopCircle, Path, X } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { BR_STATES } from "@/lib/br";

// Fix Leaflet default icon issue with bundlers
const truckIcon = new L.DivIcon({
  className: "custom-truck-marker",
  html: `<div style="background:#2563eb;color:#fff;border-radius:9999px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.3);border:3px solid #fff;font-size:14px;font-weight:800;">🚛</div>`,
  iconSize: [32, 32], iconAnchor: [16, 16],
});

const startIcon = new L.DivIcon({
  className: "custom-start-marker",
  html: `<div style="background:#10b981;color:#fff;border-radius:9999px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff;font-size:11px;font-weight:800;">A</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

export default function GPS() {
  const { user } = useAuth();
  const [origin, setOrigin] = useState("");
  const [originUf, setOriginUf] = useState("");
  const [destination, setDestination] = useState("");
  const [destinationUf, setDestinationUf] = useState("");
  const [sharing, setSharing] = useState(false);
  const [myPosition, setMyPosition] = useState(null);
  const [livePositions, setLivePositions] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null); // {id, name}
  const [routeHistory, setRouteHistory] = useState([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const watchRef = useRef(null);
  const isCompany = user?.role === "company";

  const viewRoute = async (driverId, driverName) => {
    setLoadingRoute(true);
    setSelectedDriver({ id: driverId, name: driverName });
    try {
      const { data } = await api.get(`/tracking/history/${driverId}`);
      setRouteHistory(data);
      if (data.length === 0) toast.info("Sem histórico de posições nas últimas 200 marcações.");
      else toast.success(`Trajeto de ${driverName} — ${data.length} pontos`);
    } catch (e) {
      toast.error("Erro ao carregar trajeto");
    } finally { setLoadingRoute(false); }
  };

  const clearRoute = () => {
    setSelectedDriver(null);
    setRouteHistory([]);
  };

  const open = (app) => {
    const oFull = origin + (originUf ? `, ${originUf}` : "");
    const dFull = destination + (destinationUf ? `, ${destinationUf}` : "");
    const o = encodeURIComponent(oFull);
    const d = encodeURIComponent(dFull);
    if (!destination) return;
    if (app === "gmaps") window.open(`https://www.google.com/maps/dir/?api=1${origin ? `&origin=${o}` : ""}&destination=${d}&travelmode=driving`, "_blank");
    if (app === "waze") window.open(`https://www.waze.com/ul?q=${d}&navigate=yes`, "_blank");
  };

  // Motorista: share location every 5 minutes (saves battery, as per plan A)
  const PING_INTERVAL_MS = 5 * 60 * 1000; // 5 min

  const sendPing = async () => {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude, speed, accuracy } = pos.coords;
          setMyPosition({ lat: latitude, lng: longitude });
          try {
            await api.post("/tracking/ping", { lat: latitude, lng: longitude, speed: speed || 0, accuracy: accuracy || 0 });
          } catch {}
          resolve();
        },
        (err) => { toast.error(`GPS: ${err.message}`); resolve(); },
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 }
      );
    });
  };

  const startSharing = async () => {
    if (!navigator.geolocation) { toast.error("GPS não disponível no dispositivo"); return; }
    if (!window.confirm("Ao iniciar o compartilhamento, sua localização será enviada para a empresa a cada 5 minutos.\n\nSe autônomo, só você vê sua própria localização.\n\nVocê pode parar a qualquer momento tocando em 'Parar'.\n\nConfirma?")) return;
    setSharing(true);
    try { localStorage.setItem("gps_sharing", "1"); } catch {}
    // Send first ping immediately, then every 5 min
    await sendPing();
    const id = setInterval(sendPing, PING_INTERVAL_MS);
    watchRef.current = id;
    toast.success("📍 Compartilhamento iniciado — enviando sua localização a cada 5 minutos");
  };

  const stopSharing = () => {
    if (watchRef.current != null) clearInterval(watchRef.current);
    watchRef.current = null;
    setSharing(false);
    try { localStorage.removeItem("gps_sharing"); } catch {}
    toast.info("Compartilhamento parado");
  };

  useEffect(() => () => { if (watchRef.current != null) clearInterval(watchRef.current); }, []);

  // Empresa: poll live tracking + subscribe to WS
  useEffect(() => {
    if (!isCompany) return;
    const load = async () => { try { const { data } = await api.get("/tracking/live"); setLivePositions(data); } catch {} };
    load();
    const iv = setInterval(load, 15000);
    const h = (e) => {
      if (e.detail?.entity === "tracking") {
        load();
        // If we're viewing a route and this is for that driver, refresh history
        if (selectedDriver && e.detail?.data?.driver_id === selectedDriver.id) {
          api.get(`/tracking/history/${selectedDriver.id}`).then((r) => setRouteHistory(r.data)).catch(() => {});
        }
      }
    };
    window.addEventListener("live-event", h);
    return () => { clearInterval(iv); window.removeEventListener("live-event", h); };
  }, [isCompany, selectedDriver]);

  const mapCenter = livePositions[0] ? [livePositions[0].lat, livePositions[0].lng] : (myPosition ? [myPosition.lat, myPosition.lng] : [-15.78, -47.93]); // Brasil center

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <MapTrifold size={32} weight="duotone" className="text-blue-600" /> GPS e Rastreamento
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Rota no Google Maps/Waze e rastreamento ao vivo entre motorista e empresa</p>
      </div>

      <Card><CardHeader><CardTitle className="text-lg">Calcular Rota</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Origem (opcional)</Label>
            <div className="flex gap-2">
              <div className="flex-1"><CityAutocomplete data-testid="gps-origin" value={origin} uf={originUf} onChange={(e) => setOrigin(e.target.value)} placeholder="Digite a cidade" /></div>
              <Select value={originUf} onValueChange={setOriginUf}>
                <SelectTrigger className="w-24" data-testid="gps-origin-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Destino *</Label>
            <div className="flex gap-2">
              <div className="flex-1"><CityAutocomplete data-testid="gps-destination" value={destination} uf={destinationUf} onChange={(e) => setDestination(e.target.value)} placeholder="Digite a cidade" /></div>
              <Select value={destinationUf} onValueChange={setDestinationUf}>
                <SelectTrigger className="w-24" data-testid="gps-destination-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <Button data-testid="btn-gmaps" onClick={() => open("gmaps")} disabled={!destination} className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-2"><GoogleLogo size={18} weight="fill" /> Abrir no Google Maps</Button>
            <Button data-testid="btn-waze" onClick={() => open("waze")} disabled={!destination} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-2"><NavigationArrow size={18} weight="fill" /> Abrir no Waze</Button>
          </div>
        </CardContent>
      </Card>

      {!isCompany && (
        <Card><CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-lg">Compartilhar localização ao vivo</CardTitle>
          {sharing && <Badge className="bg-emerald-600 gap-1"><Broadcast size={14} weight="fill" className="animate-pulse" /> Transmitindo</Badge>}
        </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Ative para sua empresa e seguradoras acompanharem a viagem em tempo real. A localização é enviada automaticamente sempre que o GPS detectar movimento.</p>
            {!sharing ? (
              <Button onClick={startSharing} data-testid="btn-start-tracking" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-2"><Broadcast size={18} weight="fill" /> Iniciar Compartilhamento</Button>
            ) : (
              <div className="flex gap-3 items-center">
                <Button onClick={stopSharing} data-testid="btn-stop-tracking" variant="destructive" className="gap-2"><StopCircle size={18} weight="fill" /> Parar</Button>
                {myPosition && <span className="text-sm text-muted-foreground">📍 {myPosition.lat.toFixed(4)}, {myPosition.lng.toFixed(4)}</span>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isCompany && (
        <Card><CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-lg">Rastreamento da frota — {livePositions.length} motorista(s) ativo(s)</CardTitle>
          <div className="flex gap-2 items-center">
            {selectedDriver && (
              <Badge variant="outline" className="border-emerald-300 text-emerald-800 bg-emerald-50 gap-1.5">
                <Path size={14} weight="fill" /> Trajeto: {selectedDriver.name} ({routeHistory.length} pontos)
                <button onClick={clearRoute} className="ml-1 hover:bg-emerald-200 rounded-full p-0.5" data-testid="btn-clear-route"><X size={12} /></button>
              </Badge>
            )}
            <Badge variant="outline" className="border-blue-300 text-blue-700 gap-1"><Broadcast size={14} weight="fill" className="animate-pulse" /> Ao vivo</Badge>
          </div>
        </CardHeader>
          <CardContent>
            {livePositions.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2" data-testid="drivers-panel">
                {livePositions.map((p) => (
                  <Button key={p.driver_id} size="sm" variant={selectedDriver?.id === p.driver_id ? "default" : "outline"}
                    onClick={() => viewRoute(p.driver_id, p.driver_name || "Motorista")}
                    data-testid={`btn-view-route-${p.driver_id}`}
                    className={selectedDriver?.id === p.driver_id ? "bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1.5" : "border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-1.5"}>
                    <Path size={14} weight={selectedDriver?.id === p.driver_id ? "fill" : "regular"} />
                    {p.driver_name || "Motorista"} — Ver Trajeto
                  </Button>
                ))}
                {loadingRoute && <span className="text-sm text-muted-foreground self-center">Carregando…</span>}
              </div>
            )}
            <div className="h-[520px] rounded-md overflow-hidden border" data-testid="tracking-map">
              <MapContainer center={mapCenter} zoom={livePositions.length ? 6 : 4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {/* Route polyline */}
                {routeHistory.length > 1 && (
                  <Polyline positions={routeHistory.map(p => [p.lat, p.lng])} pathOptions={{ color: "#059669", weight: 5, opacity: 0.85 }} />
                )}
                {/* Route intermediate points */}
                {routeHistory.length > 1 && routeHistory.slice(1, -1).map((p, i) => (
                  <CircleMarker key={p.id || i} center={[p.lat, p.lng]} radius={3} pathOptions={{ color: "#059669", fillColor: "#10b981", fillOpacity: 0.7, weight: 1 }}>
                    <Popup><div className="text-xs">{new Date(p.created_at).toLocaleString("pt-BR")}<br/>{p.speed ? `${(p.speed * 3.6).toFixed(0)} km/h` : "parado"}</div></Popup>
                  </CircleMarker>
                ))}
                {/* Start marker (A) */}
                {routeHistory.length > 0 && (
                  <Marker position={[routeHistory[0].lat, routeHistory[0].lng]} icon={startIcon}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-bold text-emerald-700">Início do trajeto</div>
                        <div className="text-xs text-slate-500 mt-1">{new Date(routeHistory[0].created_at).toLocaleString("pt-BR")}</div>
                      </div>
                    </Popup>
                  </Marker>
                )}
                {/* Live positions */}
                {livePositions.map((p) => (
                  <Marker key={p.driver_id} position={[p.lat, p.lng]} icon={truckIcon}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-bold text-blue-800">{p.driver_name || "Motorista"}</div>
                        <div className="text-xs text-slate-600 mt-1">Velocidade: {p.speed ? (p.speed * 3.6).toFixed(0) : 0} km/h</div>
                        <div className="text-xs text-slate-600">Precisão: {p.accuracy ? p.accuracy.toFixed(0) : 0}m</div>
                        <div className="text-xs text-slate-500 mt-1">{new Date(p.created_at).toLocaleString("pt-BR")}</div>
                        <div className="flex flex-col gap-1 mt-2">
                          <button onClick={() => viewRoute(p.driver_id, p.driver_name || "Motorista")} className="text-emerald-700 underline text-xs text-left">Ver trajeto completo</button>
                          <a href={`https://www.google.com/maps/@${p.lat},${p.lng},15z`} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">Abrir no Google Maps</a>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                <FitBounds points={routeHistory.length > 0 ? routeHistory : livePositions} />
              </MapContainer>
            </div>
            {livePositions.length === 0 && <p className="text-sm text-muted-foreground text-center mt-4">Nenhum motorista transmitindo localização. Peça para o motorista abrir a aba GPS e iniciar o compartilhamento.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
